import { joiResolver } from '@hookform/resolvers/joi';
import {
	UserHaulierRegistrationData,
	UserHaulierRegistrationSchemaWithoutClientGuid,
} from '@zeuslabsltd/common/lib/business/model/auth/haulier-registration-data';
import { ClientGuidService } from '@zeuslabsltd/react-common/lib/business/service/client-guid-service';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePlatform } from 'src/business/context/platform-context';
import { authServiceFactory } from 'src/business/service/auth-service';
import { singleEmailValidationService } from 'src/business/service/single-email-validation-service';
import { logger } from 'src/util/logger';
import { validateStrongPassword } from 'src/util/password-validation-util';

export const HaulierSignUpForm = (): React.ReactElement => {
	const navigate = useNavigate();
	const clientGuidService = new ClientGuidService();
	const [savedFormData, setSavedFormData] = useState<UserHaulierRegistrationData | null>(() => {
		const storedData = localStorage.getItem('savedFormData');
		return storedData ? JSON.parse(storedData) : null;
	});
	const { search } = useLocation();
	const urlParams = new URLSearchParams(search);
	const invitationCodeFromUrl = urlParams.get('invitation-code');

	const { isEyeQMarketplace, isAtom, platformName } = usePlatform();

	type PlatformName = string;

	const platformClasses: Record<PlatformName, { link: string; button: string }> = {
		EyeQ: { link: 'styled-link-wincanton', button: 'btn-color-wincanton' },
		ATOM: { link: 'styled-link-apollo', button: 'btn-color-apollo' },
		Zeus: { link: 'styled-link-zeus', button: 'btn-color-zeus' },
	};
	const currentPlatform = platformClasses[platformName];

	const {
		register,
		handleSubmit,
		setError,
		clearErrors,
		getValues,
		setValue,
		reset,
		formState: { errors, isValid },
	} = useForm<UserHaulierRegistrationData>({
		mode: 'onChange',
		criteriaMode: 'all',
		// TODO: Remove casting to any
		resolver: joiResolver(UserHaulierRegistrationSchemaWithoutClientGuid as any),
	});
	const hiddenInputRef = useRef<HTMLInputElement | null>(null);
	const onSubmit = async (formData: UserHaulierRegistrationData): Promise<void> => {
		const state = { ...formData, clientGuid: await clientGuidService.getOrCreate() };
		setSavedFormData(formData);
		navigate('/signup/steps', { state });
	};

	const validateEmail = async (): Promise<void> => {
		const email = getValues('email');
		if (!email) return;
		const [isValid, emailExistData] = await Promise.all([
			singleEmailValidationService().validate(email),
			authServiceFactory()
				.emailExist({ email })
				.catch((err) => logger().info('User not found')),
		]);
		const emailExist = emailExistData?.emailExist;
		if (!isValid) setError('email', { type: 'focus', message: 'Could not validate email.' }, { shouldFocus: true });
		if (emailExist) setError('email', { type: 'focus', message: 'Email already exists.' }, { shouldFocus: true });
		if (isValid && !emailExist) clearErrors('email');
	};

	const getUserEmail = async (invitationCode: string): Promise<void> => {
		authServiceFactory()
			.getMetaFromInvitationCode({ invitationCode })
			.then((result) => {
				const email = result?.email;
				if (email) setValue('email', email);
			});
	};

	const validatePasswordStrength = async (): Promise<void> => {
		const passwordValue = getValues('password');
		const errMessage: string | undefined = await validateStrongPassword(passwordValue);
		!!errMessage && setError('password', { type: 'focus', message: errMessage }, { shouldFocus: true });
	};

	useEffect(() => {
		if (savedFormData) {
			localStorage.setItem('savedFormData', JSON.stringify(savedFormData));
		} else {
			localStorage.removeItem('savedFormData');
		}
	}, [savedFormData]);

	useEffect(() => {
		if (savedFormData) {
			reset(savedFormData);
		}
	}, [savedFormData, reset]);

	useEffect(() => {
		if (invitationCodeFromUrl) getUserEmail(invitationCodeFromUrl);
	}, [invitationCodeFromUrl]);

	return (
		<Row>
			<Col xs={1}></Col>
			<Col
				xs={10}
				className="pt-5"
			>
				<h1 className="pt-3">
					<b className="font-36">Signup</b>
				</h1>
				<p className="font-13">
					Already have an account?{' '}
					<Link
						className={`font-13 ${currentPlatform?.link}`}
						to="/"
					>
						Log in here.
					</Link>
				</p>
				<Form
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<Form.Group
						id="haulier-sign-up-group-name"
						className="form-group"
						controlId="formBasicName"
					>
						<Form.Label className="font-13">Name</Form.Label>
						<Form.Control
							type="text"
							placeholder="Name"
							{...register('name')}
							isInvalid={errors.name !== undefined}
						/>
						<Form.Control.Feedback type="invalid">{errors.name?.message}</Form.Control.Feedback>
					</Form.Group>

					<Form.Group
						id="haulier-sign-up-group-last-name"
						className={`form-group ${errors.lastName ? 'has-error' : ''}`}
						controlId="formBasicLastName"
					>
						<Form.Label className="font-13">Last Name</Form.Label>
						<Form.Control
							type="text"
							placeholder="Last Name"
							{...register('lastName')}
							isInvalid={errors.lastName !== undefined}
						/>
						<Form.Control.Feedback type="invalid">{errors.lastName?.message}</Form.Control.Feedback>
					</Form.Group>

					<Form.Group
						id="haulier-sign-up-group-email"
						className={`form-group ${errors.email ? 'has-error' : ''}`}
						controlId="formBasicEmail"
					>
						<Form.Label className="font-13">Email</Form.Label>
						<Form.Control
							type="email"
							placeholder="Email"
							{...register('email')}
							isInvalid={errors.email !== undefined}
							onBlur={validateEmail}
						/>
						<Form.Control.Feedback type="invalid">{errors.email?.message}</Form.Control.Feedback>
					</Form.Group>

					<Form.Group
						id="haulier-sign-up-group-phone-number"
						className={`form-group ${errors.phoneNumber ? 'has-error' : ''}`}
						controlId="formBasicPhoneNumber"
					>
						<Form.Label className="font-13">Phone Number</Form.Label>
						<PhoneInput
							inputProps={(ref: any) => {
								register('phoneNumber');
								hiddenInputRef.current = ref;
							}}
							specialLabel={''}
							country={'gb'}
							value={savedFormData?.phoneNumber || ''}
							enableSearch={true}
							regions={['america', 'europe', 'asia', 'oceania', 'africa']}
							countryCodeEditable={false}
							onChange={(phone) => setValue('phoneNumber', `+${phone}`)}
							prefix={'+'}
							inputStyle={{ borderColor: errors.phoneNumber?.message && 'red' }}
						/>
						{errors.phoneNumber?.message && <p className="invalid-phone">{errors.phoneNumber?.message}</p>}
					</Form.Group>
					<p className="padding-top-16 font-13">
						Select which ways are best to message you about new {platformName} opportunities, features, and
						services
					</p>

					<Form.Group
						id="haulier-sign-up-group-contact-methods"
						className="form-group"
						controlId="formBasicContactMethods"
					>
						<div className="d-flex ">
							<div className="checkbox-border">
								<Form.Check
									id="email"
									type="checkbox"
									className="margin-right-32 font-13"
									label="Email"
									{...register('messageUserPreferences.messageByEmail')}
								/>
							</div>
							<div className="checkbox-border">
								<Form.Check
									id="sms"
									type="checkbox"
									className="margin-right-32 font-13"
									label="SMS"
									{...register('messageUserPreferences.messageBySms')}
								/>
							</div>
							<div className="checkbox-border">
								<Form.Check
									id="phone"
									type="checkbox"
									className="margin-right-32 font-13"
									label="Telephone"
									{...register('messageUserPreferences.messageByPhone')}
								/>
							</div>
						</div>
					</Form.Group>

					<Form.Group
						id="haulier-sign-up-group-password"
						className={`form-group ${errors.password ? 'has-error' : ''}`}
						controlId="formBasicPassword"
					>
						<Form.Label className="font-13">Password</Form.Label>
						<Form.Control
							type="password"
							placeholder="Password"
							{...register('password')}
							isInvalid={!!errors.password}
							onBlur={validatePasswordStrength}
						/>
						<Form.Control.Feedback type="invalid">{errors.password?.message}</Form.Control.Feedback>
					</Form.Group>

					<Form.Group
						id="haulier-sign-up-group-confirm-password"
						className={`form-group ${errors.confirmPassword ? 'has-error' : ''}`}
						controlId="formBasicConfirmPassword"
					>
						<Form.Label className="font-13">Confirm Password</Form.Label>
						<Form.Control
							type="password"
							placeholder="Confirm Password"
							{...register('confirmPassword')}
							isInvalid={!!errors.confirmPassword}
						/>
						<Form.Control.Feedback type="invalid">{errors.confirmPassword?.message}</Form.Control.Feedback>
					</Form.Group>

					<div className="w-100 d-flex justify-content-end">
						<Button
							variant="lg"
							className={`btn-lg btn-primary ${currentPlatform?.button}`}
							type="submit"
						>
							Continue
						</Button>
					</div>
				</Form>
			</Col>
			<Col xs={1}></Col>
		</Row>
	);
};
